import { useMutation, useQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';

import {
  getWorkflowChildren,
  getWorkflowDescribe,
  getWorkflowStackTrace,
  getWorkflowWorkers,
  listWorkflowQueries,
  runWorkflowQuery,
} from '../services/workflow-inspection.service';
import { retryInspection } from '../utils/inspection-errors';
import { mergePollers } from '../utils/workers';

import { useSensitiveMask } from './use-sensitive-mask';

/** How often a still-running execution's describe (pending activities, status) refreshes. */
const DESCRIBE_POLL_MS = 5000;

// The cache holds raw responses; each hook masks on read via `select` (memoised
// per data object) unless the session "reveal sensitive data" switch is on. The
// inspected execution's own ids are always kept readable.

/**
 * Execution detail (§3.4) — input, result/failure, pending activities and
 * attributes, masked on read. Re-polls while the execution is RUNNING so
 * pending-activity retries show up; a 404 (retention expired) is final.
 */
export function useWorkflowDescribe(workflowId: string, runId: string | null, enabled = true) {
  const { mask } = useSensitiveMask([workflowId, runId]);
  return useQuery({
    queryKey: queryKeys.workflowInspection.describe(workflowId, runId),
    queryFn: () => getWorkflowDescribe(workflowId, runId),
    // Keep every id the inspector calls/navigates with readable (and usable).
    select: (data) =>
      mask(data, [
        data.runId,
        data.firstRunId,
        data.continuedAsNewRunId,
        data.parent?.workflowId,
        data.parent?.runId,
        data.root?.workflowId,
        data.root?.runId,
        ...data.pendingChildren.flatMap((c) => [c.workflowId, c.runId]),
      ]),
    enabled: enabled && !!workflowId,
    retry: retryInspection,
    refetchInterval: (query) => (query.state.data?.status === 'RUNNING' ? DESCRIBE_POLL_MS : false),
  });
}

/** Child workflows (§3.5) for the Relationships tab — loaded lazily when the tab opens. */
export function useWorkflowChildren(
  workflowId: string,
  runId: string | null,
  {
    enabled = true,
    includeLiveStatus = false,
  }: { enabled?: boolean; includeLiveStatus?: boolean } = {},
) {
  const { mask } = useSensitiveMask([workflowId, runId]);
  return useQuery({
    queryKey: queryKeys.workflowInspection.children(workflowId, runId, includeLiveStatus),
    queryFn: () => getWorkflowChildren(workflowId, runId, includeLiveStatus),
    // Child ids open a nested inspector — they must survive masking intact.
    select: (data) =>
      mask(data, [data.runId, ...data.children.flatMap((c) => [c.workflowId, c.runId])]),
    enabled: enabled && !!workflowId,
    retry: retryInspection,
  });
}

/** Task-queue workers (§3.6), one merged row per worker; identities masked on read. */
export function useWorkflowWorkers(workflowId: string, runId: string | null, enabled = true) {
  const { mask } = useSensitiveMask([workflowId, runId]);
  return useQuery({
    queryKey: queryKeys.workflowInspection.workers(workflowId, runId),
    queryFn: () => getWorkflowWorkers(workflowId, runId),
    // Merge on raw identities first (masked ones collide), then mask the rows.
    select: (data) => mask(mergePollers(data)),
    enabled: enabled && !!workflowId,
    retry: retryInspection,
  });
}

/**
 * Live call stack (§3.7). Only meaningful while RUNNING — the caller gates
 * `enabled` on that; a 409 still surfaces as "not running" rather than retrying.
 * Never polled: it's a point-in-time snapshot refreshed on demand.
 */
export function useWorkflowStackTrace(workflowId: string, runId: string | null, enabled = true) {
  const { mask } = useSensitiveMask([workflowId, runId]);
  return useQuery({
    queryKey: queryKeys.workflowInspection.stackTrace(workflowId, runId),
    queryFn: () => getWorkflowStackTrace(workflowId, runId),
    select: mask,
    enabled: enabled && !!workflowId,
    retry: retryInspection,
    staleTime: 0,
  });
}

/** Declared query handlers (§3.8) for the Queries tab picker. */
export function useWorkflowQueryDefinitions(
  workflowId: string,
  runId: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.workflowInspection.queries(workflowId, runId),
    queryFn: () => listWorkflowQueries(workflowId, runId),
    enabled: enabled && !!workflowId,
    retry: retryInspection,
  });
}

/** Runs one query (§3.9). Returns the raw result — the Queries tab masks it on render. */
export function useRunWorkflowQuery(workflowId: string, runId: string | null) {
  return useMutation({
    mutationFn: ({ queryName, args }: { queryName: string; args: unknown[] }) =>
      runWorkflowQuery(workflowId, queryName, args, runId),
  });
}
